import { formatAmount } from "../../../../utils/hooks/dataHandler";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableRow,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  FormControl,
  FormLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { computationPeriod, validateDraft } from "../domain/preparation";
import { resolveMainComputationSnapshot } from "../../../MainTaxComputation/utils/resolveMainComputationSnapshot";
import ExternalCompanyEditDialog from "./ExternalCompanyEditDialog";

const SummaryRow = ({ label, value, total = false }) => (
  <TableRow>
    <TableCell
      component='th'
      scope='row'
      sx={{ fontWeight: total ? 700 : 400 }}
    >
      {label}
    </TableCell>
    <TableCell
      align='right'
      sx={{
        fontWeight: total ? 700 : 600,
        whiteSpace: "nowrap",
      }}
    >
      SGD {formatAmount(value, false)}
    </TableCell>
  </TableRow>
);

const sourceSummaryLines = (resolution) => {
  const detailed = resolution.groupRelief?.detailedOutput || {};
  const lossRows = () => [
    {
      id: "unutilised_losses_bf",
      label: "Unutilised Losses b/f",
      value: Number(detailed.priorYearLosses || 0),
    },
    {
      id: "current_year_losses",
      label: "Current-Year Losses",
      value: Number(detailed.currentYearLosses || 0),
    },
  ];
  const lines = [];
  let lossesAdded = false;
  (resolution.sourceLines || []).forEach((line) => {
    if (line.id === "unutilised_losses_bf") {
      lines.push(...lossRows());
      lossesAdded = true;
      return;
    }
    lines.push(
      line.id === "adjusted_profit_before_capital_allowances"
        ? {
            ...line,
            label: "Adjusted Profit",
            value: Number(detailed.adjustedProfit || 0),
          }
        : line
    );
  });
  if (!lossesAdded) lines.push(...lossRows());
  return lines;
};

const SourceSummary = ({ resolution }) => {
  const displayLines = sourceSummaryLines(resolution);
  return (
    <Box>
      {resolution.taxPeriodType === "cross_ya" && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Each YA is shown separately. The case rebate applies to the current
          YA; the preceding YA keeps its saved tax treatment.
        </Typography>
      )}
      <Table
        size='small'
        aria-label='Computation summary'
        sx={{
          "& .MuiTableCell-root": {
            px: 0,
            py: 0.75,
            verticalAlign: "top",
            borderColor: "divider",
          },
          "& th": { pr: 2, overflowWrap: "anywhere" },
          "& tr:last-child > *": { borderBottom: 0 },
        }}
      >
        <TableBody>
          {resolution.periods.map((period) => (
            <React.Fragment key={period.key}>
              <TableRow>
                <TableCell
                  colSpan={2}
                  component='th'
                  scope='colgroup'
                  sx={{ fontWeight: 700 }}
                >
                  {period.label}
                </TableCell>
              </TableRow>
              {period.steps
                ?.filter(
                  (step) =>
                    step.label !== "Chargeable Income (Before Exempt)" &&
                    (period.key !== "current" ||
                      step.label !== "Current Year Losses")
                )
                .map((step) => (
                  <SummaryRow
                    key={step.label}
                    label={step.label}
                    value={step.value}
                  />
                ))}
              <SummaryRow
                label='Assessable / Chargeable income'
                value={period.chargeableIncome}
                total
              />
              <SummaryRow
                label='Tax payable before ECI'
                value={period.taxPayableBeforeEci}
                total
              />
            </React.Fragment>
          ))}
          {displayLines.length > 0 && (
            <TableRow>
              <TableCell
                colSpan={2}
                component='th'
                scope='colgroup'
                sx={{ fontWeight: 700 }}
              >
                Computation lines used
              </TableCell>
            </TableRow>
          )}
          {displayLines.map((line) => (
            <SummaryRow key={line.id} label={line.label} value={line.value} />
          ))}
          <SummaryRow
            label='Total assessable / chargeable income'
            value={resolution.chargeableIncome}
            total
          />
          <SummaryRow
            label='Total tax payable before ECI'
            value={resolution.taxPayableBeforeEci}
            total
          />
        </TableBody>
      </Table>
    </Box>
  );
};

export default function CompanyDetailsStep({ workspace: w, attempted }) {
  const [externalEditOpen, setExternalEditOpen] = useState(false);
  const { reviewFocusField, setReviewFocusField } = w;
  useEffect(() => {
    if (!reviewFocusField) return;
    const frame = requestAnimationFrame(() => {
      const element = document.getElementById(`gro-${reviewFocusField}`);
      element?.scrollIntoView?.({ block: "center" });
      element?.focus({ preventScroll: true });
      setReviewFocusField(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [reviewFocusField, setReviewFocusField]);
  const draft = w.drafts[w.activeId];
  if (!draft) return null;
  const errors = validateDraft(draft);
  const loading = draft.sourceStatus === "loading";
  const disabled = loading;
  const showErrors =
    attempted || w.companyChecksAttempted || draft.sourceFiguresConfirmed;
  const patch = (value) => w.updateDraft(draft.id, value);
  const period = computationPeriod(draft.computation);
  const hasStartupValue = Boolean(
    draft.sourceDoc &&
    Object.prototype.hasOwnProperty.call(draft.sourceDoc, "isStartup")
  );
  const startupConfig = draft.sourceDoc?.isStartup;
  const startupValue = (periodKey) => {
    if (!hasStartupValue) return null;
    if (
      startupConfig &&
      typeof startupConfig === "object" &&
      !Array.isArray(startupConfig)
    )
      return Boolean(
        startupConfig[periodKey] ??
        startupConfig.current ??
        startupConfig.preceding ??
        false
      );
    return Boolean(startupConfig);
  };
  const assessmentYear = Number(draft.computation.yearOfAssessment);
  const startupPeriods =
    draft.computation.taxPeriodType === "cross_ya"
      ? [
          {
            label: `YA ${assessmentYear - 1}`,
            value: startupValue("preceding"),
          },
          { label: `YA ${assessmentYear}`, value: startupValue("current") },
        ]
      : [{ label: null, value: startupValue("current") }];
  const allCompaniesReadyForBulkCheck =
    w.selected.length > 0 &&
    w.selected.every(
      (item) => item.sourceStatus === "loaded" && Boolean(item.sourceDoc)
    );
  const markAllYesAndChecked = () => {
    if (!allCompaniesReadyForBulkCheck) return;
    w.selected.forEach((item) =>
      w.updateDraft(item.id, {
        shareholdingMaintainedThroughout: true,
        conditionMetOnFinalDay: true,
        continuousQualifyingDays: "",
        sourceFiguresConfirmed: true,
      })
    );
  };
  let resolution = null;
  let resolutionError = "";
  if (draft.sourceStatus === "loaded") {
    try {
      resolution = resolveMainComputationSnapshot({
        computation: draft.computation,
        doc: draft.sourceDoc,
        settings: w.settings,
      });
    } catch (error) {
      resolutionError =
        error?.message || "The saved computation could not be resolved.";
    }
  }
  const yesNo = (key, label, onChange) => (
    <FormControl disabled={disabled} error={showErrors && Boolean(errors[key])}>
      <FormLabel
        id={`gro-${key}-label`}
        sx={{ color: "text.primary", fontWeight: 600 }}
      >
        {label}
      </FormLabel>
      <RadioGroup
        aria-labelledby={`gro-${key}-label`}
        value={draft[key] === "" ? "" : String(draft[key])}
        onChange={(event) =>
          onChange
            ? onChange(event.target.value === "true")
            : patch({ [key]: event.target.value === "true" })
        }
        row
      >
        <FormControlLabel
          value='true'
          control={<Radio id={`gro-${key}`} />}
          label='Yes'
        />
        <FormControlLabel value='false' control={<Radio />} label='No' />
      </RadioGroup>
      {showErrors && errors[key] && (
        <FormHelperText>{errors[key]}</FormHelperText>
      )}
    </FormControl>
  );

  return (
    <Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "250px minmax(0, 1fr)" },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Paper
          variant='outlined'
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            position: { md: "sticky" },
            top: 0,
          }}
        >
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant='subtitle1' fontWeight={750}>
              Participating Companies
            </Typography>

            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ mt: 0.5, mb: 1 }}
            >
              Review each company's figures and confirm the shareholding
              information below.
            </Typography>
            <Button
              size='small'
              variant='outlined'
              color='success'
              fullWidth
              startIcon={<PlaylistAddCheckIcon fontSize='small' />}
              disabled={w.confirmedCount === w.selected.length}
              onClick={markAllYesAndChecked}
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                whiteSpace: "nowrap",
                bgcolor: "success.main",
                color: "common.white",
                borderColor: "success.main",
                "&:hover": {
                  bgcolor: "success.light",
                  borderColor: "success.main",
                },
                "&:disabled": {
                  bgcolor: "action.disabledBackground",
                  color: "primary.main",
                  borderColor: "action.disabledBackground",
                },
              }}
            >
              {w.confirmedCount === w.selected.length
                ? "All companies ready"
                : `Set all to Yes (${w.confirmedCount}/${w.selected.length})`}
            </Button>
          </Box>
          <List aria-label='Company details navigation'>
            {w.selected.map((item, index) => (
              <ListItemButton
                key={item.id}
                selected={item.id === draft.id}
                onClick={() => w.setActiveId(item.id)}
              >
                <ListItemText
                  primary={`${index + 1}. ${item.name}`}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 650 }}
                  secondary={
                    item.confirmed
                      ? "Ready"
                      : item.sourceStatus === "loaded"
                        ? "Needs your check"
                        : item.sourceStatus === "error"
                          ? "Retry needed"
                          : "Loading computation"
                  }
                />
                {item.confirmed && (
                  <CheckCircleOutlineIcon color='success' fontSize='small' />
                )}
              </ListItemButton>
            ))}
          </List>
        </Paper>
        <Stack spacing={3} sx={{ minWidth: 0 }}>
          <Paper
            id='gro-company'
            tabIndex={-1}
            variant='outlined'
            sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              justifyContent='space-between'
              spacing={2}
            >
              <Box>
                <Typography variant='h6' fontWeight={750}>
                  {draft.name}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  {draft.uen} · YA {draft.computation.yearOfAssessment} · FYE{" "}
                  {String(draft.computation.financialYearEnd).slice(0, 10)}
                </Typography>
                <Stack direction='row' gap={1} flexWrap='wrap' sx={{ mt: 1 }}>
                  {draft.isExternal && (
                    <Chip
                      size='small'
                      label='EXTERNAL'
                      color='secondary'
                      variant='outlined'
                      sx={{ fontWeight: 750 }}
                    />
                  )}
                  <Chip
                    size='small'
                    label={
                      draft.computation.taxPeriodType === "cross_ya"
                        ? "Cross-YA"
                        : "Single YA"
                    }
                  />
                  <Chip
                    size='small'
                    label={
                      resolution?.companyType === "investment"
                        ? "Investment company"
                        : "Trading company"
                    }
                  />
                </Stack>
                <Stack
                  direction='row'
                  alignItems='center'
                  gap={1}
                  flexWrap='wrap'
                  sx={{ mt: 1.25 }}
                >
                  <Typography variant='body2' fontWeight={650}>
                    Qualifies for Start-Up Tax Exemption
                  </Typography>
                  {startupPeriods.map(({ label, value }) => (
                    <Chip
                      key={label || "current"}
                      size='small'
                      label={`${label ? `${label} · ` : ""}${
                        value === null ? "Not available" : value ? "Yes" : "No"
                      }`}
                      color={value === true ? "success" : "default"}
                      variant={value === true ? "filled" : "outlined"}
                      sx={{ fontWeight: 650 }}
                    />
                  ))}
                </Stack>
              </Box>
              {draft.isExternal && (
                <Button
                  variant='outlined'
                  startIcon={<EditOutlinedIcon />}
                  disabled={disabled}
                  onClick={() => setExternalEditOpen(true)}
                  sx={{ flexShrink: 0, textTransform: "none" }}
                >
                  Edit external details
                </Button>
              )}
            </Stack>

            {showErrors && errors.company && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {errors.company}
              </Alert>
            )}
            <Box id='gro-source' tabIndex={-1}>
              {loading ? (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    Reading the saved Main Tax Computation…
                  </Typography>
                </Box>
              ) : draft.sourceStatus === "error" ? (
                <Alert
                  severity='error'
                  sx={{ mt: 2 }}
                  action={
                    !draft.confirmed ? (
                      <Button onClick={w.retrySnapshot}>Retry</Button>
                    ) : null
                  }
                >
                  {draft.sourceNote}
                </Alert>
              ) : resolutionError ? (
                <Alert severity='error' sx={{ mt: 2 }}>
                  {resolutionError}{" "}
                  {draft.isExternal
                    ? "Correct the Excel data and upload the company again."
                    : "Re-save the Main Tax Computation and retry."}
                </Alert>
              ) : (
                <Alert severity='info' sx={{ mt: 2 }}>
                  {draft.sourceNote}
                  {draft.sourceSavedAt && (
                    <Typography variant='caption' display='block'>
                      Source saved:{" "}
                      {new Date(draft.sourceSavedAt).toLocaleString("en-SG")}
                    </Typography>
                  )}
                </Alert>
              )}
              <br />
              {resolution && <SourceSummary resolution={resolution} />}
            </Box>

            <Box
              sx={{
                mt: 3,
                p: 2,
                border: "2px solid",
                borderColor: draft.confirmed ? "success.main" : "primary.main",
                borderRadius: 2,
              }}
            >
              <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                Company Review
              </Typography>

              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Review the shareholding information and verify the figures
                before proceeding.
              </Typography>
              <Stack spacing={2}>
                {yesNo(
                  "shareholdingMaintainedThroughout",
                  "Was the 75% shareholding condition met for the whole basis period?",
                  (value) =>
                    patch({
                      shareholdingMaintainedThroughout: value,
                      conditionMetOnFinalDay: "",
                      continuousQualifyingDays: "",
                    })
                )}
                {draft.shareholdingMaintainedThroughout === false && (
                  <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 2 }}>
                    <Stack spacing={2}>
                      {yesNo(
                        "conditionMetOnFinalDay",
                        "75% condition satisfied on the final day?"
                      )}
                      <TextField
                        id='gro-continuousQualifyingDays'
                        label='Continuous qualifying days'
                        type='number'
                        value={draft.continuousQualifyingDays}
                        disabled={disabled}
                        inputProps={{ min: 1, max: period?.days, step: 1 }}
                        onChange={(event) =>
                          patch({
                            continuousQualifyingDays: event.target.value,
                          })
                        }
                        error={
                          showErrors && Boolean(errors.continuousQualifyingDays)
                        }
                        helperText={
                          showErrors && errors.continuousQualifyingDays
                            ? errors.continuousQualifyingDays
                            : `Tax computation period: ${period?.days} days (${period?.start} to ${period?.end}).`
                        }
                      />
                    </Stack>
                  </Box>
                )}
              </Stack>
              <Box
                sx={{ mt: 3, p: 2, bgcolor: "action.hover", borderRadius: 2 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      id='gro-sourceFiguresConfirmed'
                      checked={draft.sourceFiguresConfirmed}
                      disabled={disabled || !resolution}
                      onChange={(event) =>
                        patch({ sourceFiguresConfirmed: event.target.checked })
                      }
                    />
                  }
                  label={
                    draft.isExternal
                      ? "I checked these figures against the external company details."
                      : "I checked these figures against the saved Main Tax Computation."
                  }
                />
                {showErrors && errors.sourceFiguresConfirmed && (
                  <Typography variant='caption' color='error' display='block'>
                    {errors.sourceFiguresConfirmed}
                  </Typography>
                )}
              </Box>

              <Typography
                role='status'
                variant='body2'
                color={draft.confirmed ? "success.main" : "text.secondary"}
                sx={{ mt: 1 }}
              >
                {draft.confirmed
                  ? "Ready. Check another company or select Review & calculate."
                  : draft.sourceFiguresConfirmed
                    ? "Figures checked. Complete the shareholding details above to finish."
                    : "Tick the figures check after reviewing this company."}
              </Typography>
            </Box>
          </Paper>
        </Stack>
      </Box>
      {draft.isExternal && (
        <ExternalCompanyEditDialog
          open={externalEditOpen}
          company={draft}
          onClose={() => setExternalEditOpen(false)}
          onSave={(input) => w.updateExternalCompany(draft.id, input)}
        />
      )}
    </Box>
  );
}
